import { Typography } from '@euler/app/components/typography';
import { aiColors, tint } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiColor, kBiVizStyleOptionNames } from '@euler/model/bi/viz';
import { ChevronDown, ChevronUp } from 'lucide-react-native';
import { memo, useState } from 'react';
import { Pressable, View } from 'react-native';
import { ColorChoices } from '../ColorChoices';
import { StyleGroup } from '../StyleGroup';
import { BiColorTarget } from '../functions/biColorTargets';
import { biWithStyleOption } from '../functions/biStyleEdit';
import { BiStyleOptionProps } from '../functions/biStyleOptionProps';

/** A color per series or slice, over the palette's. */
export const SeriesColorOptions = memo(
  ({
    style,
    targets,
    onChange,
  }: BiStyleOptionProps & { targets: BiColorTarget[] }) => {
    const { rpx } = useResponsiveContext();
    const [open, setOpen] = useState<string | null>(null);
    const colors = style?.seriesColors ?? {};
    const pick = (key: string, color: BiColor | undefined) => {
      const next = Object.fromEntries(
        Object.entries({ ...colors, [key]: color }).filter(
          (entry): entry is [string, BiColor] => entry[1] !== undefined,
        ),
      );
      onChange(
        biWithStyleOption(
          style,
          'seriesColors',
          Object.keys(next).length ? next : undefined,
        ),
      );
    };
    return (
      <StyleGroup
        title={kBiVizStyleOptionNames.seriesColors}
        onReset={
          style?.seriesColors
            ? () =>
                onChange(biWithStyleOption(style, 'seriesColors', undefined))
            : null
        }
      >
        {targets.length === 0 ? (
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            预览出数据后可以给每个系列选颜色。
          </Typography.Text>
        ) : null}
        {targets.map(target => (
          <View key={target.key} style={{ gap: rpx(6) }}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={`「${target.name}」的颜色`}
              accessibilityState={{ expanded: open === target.key }}
              onPress={() => setOpen(open === target.key ? null : target.key)}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: rpx(8),
                paddingVertical: rpx(4),
              }}
            >
              <View
                style={{
                  width: rpx(14),
                  height: rpx(14),
                  borderRadius: rpx(7),
                  backgroundColor: target.color,
                  borderWidth: colors[target.key] ? 2 : 0,
                  borderColor: tint(aiColors.foreground, 0.4),
                }}
              />
              <Typography.Text
                size={rpx(12)}
                color={aiColors.foreground}
                numberOfLines={1}
                style={{ flex: 1 }}
              >
                {target.name}
              </Typography.Text>
              {open === target.key ? (
                <ChevronUp size={14} color={aiColors.mutedForeground} />
              ) : (
                <ChevronDown size={14} color={aiColors.mutedForeground} />
              )}
            </Pressable>
            {open === target.key ? (
              <ColorChoices
                value={colors[target.key]}
                onPick={color => pick(target.key, color)}
              />
            ) : null}
          </View>
        ))}
      </StyleGroup>
    );
  },
);
